/* src/components/tutor/tutor_announcements/dashboard/TutorAnnouncementPostView.js */
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import {
  addComment,
  deleteComment,
  markAnnouncementRead,
  subscribeComments,
} from "../TutorAnnouncementsLogic";
import ToastHost from "./ToastHost";

import {
  FiArrowLeft,
  FiMessageSquare,
  FiTag,
  FiUser,
  FiClock,
  FiCornerDownRight,
  FiUsers,
} from "react-icons/fi";
import { BsPinFill } from "react-icons/bs";
import "./TutorAnnouncementPostView.css";

function fmtDate(ts) {
  const ms = ts?.toMillis?.() ?? null;
  if (!ms) return "";
  const d = new Date(ms);
  return (
    d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " at " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  );
}

export default function TutorAnnouncementPostView({ me, announcementId, onBack }) {
  const [a, setA] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "announcements", announcementId), (snap) => {
      setA(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub?.();
  }, [announcementId]);

  useEffect(() => {
    const unsub = subscribeComments(announcementId, (rows) => setComments(rows || []));
    return () => unsub?.();
  }, [announcementId]);

  // Auto-mark as read on open
  useEffect(() => {
    if (!announcementId) return;
    markAnnouncementRead(announcementId).catch(() => {});
  }, [announcementId]);

  const audienceLabel = useMemo(() => {
    if (!a) return "";
    return a.audienceType === "all"
      ? "All Tutors"
      : `${(a.audienceUserIds || []).length} Tutors`;
  }, [a]);

  const submitComment = async () => {
    if (!a) return;
    if (a.commentsEnabled === false) {
      setToast({ kind: "info", msg: "Comments are disabled." });
      return;
    }
    if (!text.trim()) return;
    try {
      await addComment(announcementId, text);
      setText("");
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed to comment." });
    }
  };

  const handleDeleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment(announcementId, cid);
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed." });
    }
  };

  if (!a) {
    return (
      <div className="taPost">
        <div className="taPostContainer">
          <button className="taPostBackBtn" onClick={onBack} type="button">
            <FiArrowLeft size={16} /> Announcements
          </button>
          <div className="taPostMissing">This post no longer exists.</div>
        </div>
        <ToastHost toast={toast} onClear={() => setToast(null)} />
      </div>
    );
  }

  return (
    <div className="taPost">
      <div className="taPostContainer">
        {/* Header */}
        <div className="taPostHead">
          <button className="taPostBackBtn" onClick={onBack} type="button">
            <FiArrowLeft size={16} /> Announcements
          </button>
        </div>

        {/* Post card */}
        <div className="taPostCard">
          {a.pinned && (
            <div className="taPostPinnedBadge">
              <BsPinFill size={11} /> Pinned
            </div>
          )}
          <h1 className="taPostMainTitle">{a.title || "(Untitled)"}</h1>

          <div className="taPostMetaRow">
            <span className="taPostMetaItem">
              <FiUser size={13} /> {a.createdByName || "Admin"}
            </span>
            <span className="taPostMetaDot">·</span>
            <span className="taPostMetaItem">
              <FiClock size={13} /> {fmtDate(a.createdAt)}
            </span>
            <span className="taPostMetaDot">·</span>
            <span className="taPostMetaItem">
              <FiTag size={12} /> {(a.category || "General").trim() || "General"}
            </span>
            <span className="taPostMetaDot">·</span>
            <span className="taPostMetaItem">
              <FiUsers size={12} /> {audienceLabel}
            </span>
          </div>

          <div className="taPostDivider" />

          <div className="taPostContent" dangerouslySetInnerHTML={{ __html: a.contentHtml || "" }} />
        </div>

        {/* Comments */}
        <div className="taCommentsCard" id="taCommentBox">
          <div className="taCommentsHead">
            <FiMessageSquare size={16} /> Comments ({comments.length})
          </div>

          {a.commentsEnabled === false ? (
            <div className="taCommentsDisabled">Comments are disabled for this post.</div>
          ) : (
            <div className="taCommentComposer">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
              />
              <div className="taCommentComposerFoot">
                <button className="taPostBtn accent" onClick={submitComment} type="button">
                  <FiCornerDownRight size={14} /> Post Comment
                </button>
              </div>
            </div>
          )}

          <div className="taCommentsList">
            {comments.length === 0 ? (
              <div className="taCommentsEmpty">No comments yet. Be the first to start the discussion!</div>
            ) : (
              comments.map((c) => {
                const mine = c.uid && me?.uid && c.uid === me.uid;
                return (
                  <div key={c.id} className="taComment">
                    <div className="taCommentAvatar">{(c.userName || "U").slice(0, 1).toUpperCase()}</div>
                    <div className="taCommentBody">
                      <div className="taCommentTop">
                        <span className="taCommentName">{c.userName || "User"}</span>
                        {mine && (
                          <button
                            className="taCommentDelete"
                            onClick={() => handleDeleteComment(c.id)}
                            type="button"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="taCommentText">{c.text || ""}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <ToastHost toast={toast} onClear={() => setToast(null)} />
    </div>
  );
}
